import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import { IMAGES } from "../lib/images";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import Newsletter from "../components/Newsletter";

const About = () => {
  const { t } = useLang();
  const a = t.about;

  return (
    <div data-testid="about-page" className="bg-obsidian">
      <Seo pageKey="about" />
      {/* Intro */}
      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-20 md:px-12 md:pt-52">
        <span className="text-xs uppercase tracking-[0.3em] text-champagne">{a.overline}</span>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
          <MaskedLines lines={[a.title]} startDelay={0.2} />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-white/60 md:text-2xl">{a.intro}</p>
        </Reveal>
      </section>

      {/* Founder */}
      <section className="border-t border-white/10 bg-obsidian-soft py-24 md:py-36">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:grid-cols-12 md:px-12">
          <Reveal className="md:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
              <img src={IMAGES.founder} alt={a.founderName} className="h-full w-full object-cover object-[72%_top]" data-testid="founder-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 to-transparent" />
            </div>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">{a.founderRole}</span>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-white md:text-6xl">{a.founderName}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base font-light leading-relaxed text-white/65 md:text-lg">{a.founderBio}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <blockquote className="mt-10 border-l-2 border-champagne pl-6 font-serif text-2xl font-light italic leading-snug text-white md:text-3xl">
                “{a.founderQuote}”
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Facts */}
      <section className="border-t border-white/10 bg-obsidian py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-x-8 gap-y-12 px-6 md:grid-cols-4 md:px-12">
          {a.facts.map((f, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center md:text-left">
              <div className="font-serif text-5xl font-light text-champagne md:text-7xl" data-testid={`fact-${i}`}>{f.v}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-white/50">{f.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="border-t border-white/10 bg-obsidian-soft py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{a.timelineTitle}</span>
          </Reveal>
          <div className="mt-14 space-y-0">
            {a.timeline.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="group grid items-baseline gap-2 border-t border-white/10 py-8 md:grid-cols-12 md:gap-8" data-testid={`timeline-${i}`}>
                  <span className="font-serif text-xl italic text-champagne md:col-span-3">{item.year}</span>
                  <h3 className="font-serif text-2xl font-light text-white transition-colors group-hover:text-champagne md:col-span-5 md:text-3xl">{item.t}</h3>
                  <p className="text-sm font-light leading-relaxed text-white/50 md:col-span-4">{item.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-obsidian py-28 text-center md:py-40">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-4xl font-light leading-tight md:text-6xl">{a.cta}</h2>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="group mt-10 inline-flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
            >
              {t.nav.book}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FaqSection pageKey="about" />

      <Newsletter />
    </div>
  );
};

export default About;
